import {
  getDidomiAnalyticsPurposeId,
  getDidomiPostHogVendorIds,
  getPrimaryAnalyticsVendorId,
} from './profileAnalytics';
import { applyGrantedGa4Loaders } from './ga4ProfileAnalytics';
import { allowPostHogInit, shutdownPostHog } from './postHogProfile';
import { setPostHogCaptureAllowedChecker } from './postHogConsentGate';

/** @type {'accepted' | 'declined' | null} */
let cachedConsent = null;

let userClickedDisagree = false;
let userHasInteracted = false;
let pendingAnalyticsPurposeDecline = false;
let pendingAnalyticsVendorDecline = false;
let pendingPostHogVendorDecline = false;

const changeListeners = new Set();

function notifyChangeListeners() {
  changeListeners.forEach((callback) => {
    try {
      callback();
    } catch (e) {
      /* ignore listener errors */
    }
  });
}

function publishConsentState() {
  applyGrantedGa4Loaders(ga4ShouldLoad());
  applyPostHogConsentState();
  notifyChangeListeners();
}

function purposeLookupKeys(id) {
  if (!id) return [];
  if (id.indexOf('c:') === 0) {
    return [id, id.slice(2)];
  }
  return [id, `c:${id}`];
}

function vendorLookupKeys(vendorId) {
  return purposeLookupKeys(vendorId);
}

function vendorIdsMatch(a, b) {
  if (!a || !b) return false;
  const keysA = vendorLookupKeys(a);
  const keysB = vendorLookupKeys(b);
  return keysA.some(keyA => keysB.indexOf(keyA) !== -1);
}

function getDidomi() {
  if (typeof window === 'undefined') return null;
  return window.Didomi || null;
}

function isDidomiOptOutRegime() {
  const Didomi = getDidomi();
  if (!Didomi || typeof Didomi.isConsentRequired !== 'function') {
    return false;
  }
  try {
    return Didomi.isConsentRequired() === false;
  } catch (e) {
    return false;
  }
}

function getPurposeName(purposeId) {
  const Didomi = getDidomi();
  if (!Didomi || !purposeId) return '';
  try {
    if (typeof Didomi.getPurpose === 'function') {
      const purpose = Didomi.getPurpose(purposeId);
      if (purpose && purpose.name) {
        return String(purpose.name);
      }
    }
  } catch (e) {
    /* ignore */
  }
  return '';
}

function isAnalyticsRelatedPurpose(purposeId) {
  if (!purposeId) return false;

  const configuredKeys = purposeLookupKeys(getDidomiAnalyticsPurposeId());
  const keys = purposeLookupKeys(purposeId);
  for (let i = 0; i < keys.length; i += 1) {
    if (configuredKeys.indexOf(keys[i]) !== -1) return true;
    if (/analytics|measure_content|measure_ad|market_research|statistic/i.test(keys[i])) {
      return true;
    }
  }
  return /analytics|measure|statistic/i.test(getPurposeName(purposeId));
}

function isAnalyticsRelatedVendor(vendorId) {
  if (!vendorId) return false;
  if (vendorIdsMatch(vendorId, getPrimaryAnalyticsVendorId())) {
    return true;
  }
  return /googleana|google analytics|ga4/i.test(String(vendorId));
}

function isPostHogRelatedVendor(vendorId) {
  if (!vendorId) return false;
  const posthogIds = getDidomiPostHogVendorIds();
  for (let i = 0; i < posthogIds.length; i += 1) {
    if (vendorIdsMatch(vendorId, posthogIds[i])) {
      return true;
    }
  }
  return /posthog/i.test(String(vendorId));
}

function applyPostHogConsentState() {
  if (!hasAcceptedAnalytics() || !hasAcceptedPostHog()) {
    shutdownPostHog();
    return;
  }
  allowPostHogInit();
}

function refreshPostHogDeclineFromDidomi() {
  const Didomi = getDidomi();
  if (!Didomi) return false;

  let status = null;
  try {
    status = Didomi.getCurrentUserStatus();
  } catch (e) {
    return false;
  }

  if (isPostHogExplicitlyDenied(status)) {
    pendingPostHogVendorDecline = true;
    applyPostHogConsentState();
    return true;
  }

  pendingPostHogVendorDecline = false;
  return false;
}

function collectAnalyticsPurposeIds(status) {
  const ids = [];
  const configured = getDidomiAnalyticsPurposeId();
  if (configured) ids.push(configured);

  const addId = (id) => {
    if (!id || ids.indexOf(id) !== -1) return;
    ids.push(id);
  };

  const purposes = status && status.purposes;
  if (purposes) {
    Object.keys(purposes).forEach((purposeId) => {
      if (isAnalyticsRelatedPurpose(purposeId)) addId(purposeId);
    });
  }

  const Didomi = getDidomi();
  if (Didomi && typeof Didomi.getPurposes === 'function') {
    try {
      const list = Didomi.getPurposes() || [];
      for (let i = 0; i < list.length; i += 1) {
        const item = list[i];
        if (item && item.id && isAnalyticsRelatedPurpose(item.id)) {
          addId(item.id);
        }
      }
    } catch (e) {
      /* ignore */
    }
  }

  return ids;
}

function isPurposeConsentDenied(purposeId, status) {
  const keys = purposeLookupKeys(purposeId);
  const Didomi = getDidomi();

  if (Didomi && typeof Didomi.getUserConsentStatusForPurpose === 'function') {
    try {
      for (let i = 0; i < keys.length; i += 1) {
        if (Didomi.getUserConsentStatusForPurpose(keys[i]) === false) {
          return true;
        }
      }
    } catch (e) {
      /* ignore */
    }
  }

  if (status && status.purposes) {
    for (let i = 0; i < keys.length; i += 1) {
      const purpose = status.purposes[keys[i]];
      if (purpose && purpose.enabled === false) {
        return true;
      }
    }
  }

  return false;
}

function isDidomiPurposeConsentDenied(status) {
  const purposeIds = collectAnalyticsPurposeIds(status);
  for (let i = 0; i < purposeIds.length; i += 1) {
    if (isPurposeConsentDenied(purposeIds[i], status)) {
      return true;
    }
  }
  return false;
}

function getVendorConsent(status, vendorId) {
  const Didomi = getDidomi();
  const keys = vendorLookupKeys(vendorId);
  let sawFalse = false;

  if (Didomi && typeof Didomi.getUserConsentStatusForVendor === 'function') {
    try {
      for (let i = 0; i < keys.length; i += 1) {
        const apiValue = Didomi.getUserConsentStatusForVendor(keys[i]);
        if (apiValue === true) return true;
        if (apiValue === false) sawFalse = true;
      }
    } catch (e) {
      /* ignore */
    }
  }

  if (status && status.vendors) {
    for (let i = 0; i < keys.length; i += 1) {
      const vendor = status.vendors[keys[i]];
      if (vendor && vendor.enabled === false) {
        return false;
      }
    }
  }

  if (sawFalse) return false;
  return null;
}

function isDidomiAnalyticsVendorDenied(status) {
  return getVendorConsent(status, getPrimaryAnalyticsVendorId()) === false;
}

function vendorPresentInStatus(status, vendorId) {
  const keys = vendorLookupKeys(vendorId);
  if (status && status.vendors) {
    for (let i = 0; i < keys.length; i += 1) {
      if (status.vendors[keys[i]]) return true;
    }
  }
  return false;
}

function isDidomiVendorConsentGranted(vendorId) {
  const Didomi = getDidomi();
  if (!Didomi || typeof Didomi.getUserConsentStatusForVendor !== 'function') {
    return false;
  }
  const keys = vendorLookupKeys(vendorId);
  try {
    for (let i = 0; i < keys.length; i += 1) {
      if (Didomi.getUserConsentStatusForVendor(keys[i]) === true) {
        return true;
      }
    }
  } catch (e) {
    /* ignore */
  }
  return false;
}

function isVendorEnabledInStatus(status, vendorId) {
  const keys = vendorLookupKeys(vendorId);
  for (let i = 0; i < keys.length; i += 1) {
    const vendor = status && status.vendors && status.vendors[keys[i]];
    if (vendor && vendor.enabled === true) return true;
  }
  return isDidomiVendorConsentGranted(vendorId);
}

function readGcmAnalyticsStorage(updatesOnly = false) {
  if (typeof window === 'undefined' || !window.dataLayer) return null;

  let latest = null;
  window.dataLayer.forEach((entry) => {
    if (!Array.isArray(entry) || entry[0] !== 'consent') return;
    if (updatesOnly && entry[1] !== 'update') return;
    const map = entry[2];
    if (map && typeof map.analytics_storage === 'string') {
      latest = map.analytics_storage;
    }
  });
  return latest;
}

function isAnalyticsExplicitlyDeclined(status) {
  if (userClickedDisagree) return true;
  if (readGcmAnalyticsStorage(true) === 'denied') return true;
  if (isDidomiPurposeConsentDenied(status)) return true;
  if (isDidomiAnalyticsVendorDenied(status)) return true;
  return false;
}

function isAnalyticsEnabledInStatus(status) {
  if (isDidomiPurposeConsentDenied(status)) {
    return false;
  }

  if (isDidomiAnalyticsVendorDenied(status)) {
    return false;
  }

  if (status) {
    const primary = getPrimaryAnalyticsVendorId();
    if (getVendorConsent(status, primary) === true) {
      return true;
    }

    if (!vendorPresentInStatus(status, primary)) {
      if (getVendorConsent(status, 'google') === true) {
        return true;
      }
    }
  }

  if (isDidomiOptOutRegime() && !isAnalyticsExplicitlyDeclined(status)) {
    return true;
  }

  return false;
}

function isPostHogExplicitlyDenied(status) {
  const vendorIds = getDidomiPostHogVendorIds();
  for (let i = 0; i < vendorIds.length; i += 1) {
    if (getVendorConsent(status, vendorIds[i]) === false) {
      return true;
    }
  }
  return false;
}

function isPostHogEnabledInStatus(status) {
  const vendorIds = getDidomiPostHogVendorIds();
  if (!vendorIds.length) {
    return false;
  }

  if (isDidomiOptOutRegime()) {
    return !isPostHogExplicitlyDenied(status);
  }

  if (!status) {
    return false;
  }

  for (let i = 0; i < vendorIds.length; i += 1) {
    if (isVendorEnabledInStatus(status, vendorIds[i])) {
      return true;
    }
  }
  return false;
}

function isConfiguredAnalyticsPurposeDisabled(status) {
  const purposeId = getDidomiAnalyticsPurposeId();
  return isPurposeConsentDenied(purposeId, status);
}

function refreshAnalyticsDeclineFromDidomi() {
  const Didomi = getDidomi();
  if (!Didomi) return false;

  let status = null;
  try {
    status = Didomi.getCurrentUserStatus();
  } catch (e) {
    return false;
  }

  const purposeOff = isDidomiPurposeConsentDenied(status)
    || isConfiguredAnalyticsPurposeDisabled(status);
  const vendorOff = isDidomiAnalyticsVendorDenied(status);

  if (purposeOff || vendorOff) {
    pendingAnalyticsPurposeDecline = purposeOff;
    pendingAnalyticsVendorDecline = vendorOff;
    if (purposeOff) {
      pendingPostHogVendorDecline = true;
    }
    markAnalyticsDeclined();
    applyPostHogConsentState();
    return true;
  }

  pendingAnalyticsPurposeDecline = false;
  pendingAnalyticsVendorDecline = false;
  if (!isPostHogExplicitlyDenied(status)) {
    pendingPostHogVendorDecline = false;
  }
  return false;
}

function applyDidomiVendorStatusToCache() {
  if (refreshAnalyticsDeclineFromDidomi()) {
    return;
  }

  const Didomi = getDidomi();
  if (!Didomi) return;

  let status = null;
  try {
    status = Didomi.getCurrentUserStatus();
  } catch (e) {
    return;
  }

  if (pendingAnalyticsPurposeDecline || pendingAnalyticsVendorDecline) {
    setAnalyticsConsent('declined');
    return;
  }

  if (isAnalyticsEnabledInStatus(status)) {
    userClickedDisagree = false;
    setAnalyticsConsent('accepted');
    return;
  }

  if (isAnalyticsExplicitlyDeclined(status) || userHasInteracted) {
    setAnalyticsConsent('declined');
    return;
  }

  if (!Didomi.isConsentRequired()) {
    setAnalyticsConsent('accepted');
  }
}

/** Didomi may update consent asynchronously after UI events — match Sencha login (0 + 100 ms). */
function scheduleConsentResync() {
  window.setTimeout(syncProfileConsentFromDidomi, 0);
  window.setTimeout(syncProfileConsentFromDidomi, 100);
}

/** Re-read Didomi and apply GA4 + PostHog surface loaders (preferences save / consent.changed). */
export function syncProfileConsentFromDidomi() {
  refreshAnalyticsDeclineFromDidomi();
  refreshPostHogDeclineFromDidomi();
  applyDidomiVendorStatusToCache();
  applyGrantedGa4Loaders(ga4ShouldLoad());
  applyPostHogConsentState();
}

function syncConsentAfterUserAction() {
  markUserInteracted();
  if (refreshAnalyticsDeclineFromDidomi()) {
    scheduleConsentResync();
    return;
  }
  applyDidomiVendorStatusToCache();
  scheduleConsentResync();
}

function ga4ShouldLoad() {
  if (cachedConsent === 'declined' || userClickedDisagree) {
    return false;
  }
  if (pendingAnalyticsPurposeDecline || pendingAnalyticsVendorDecline) {
    return false;
  }
  return hasAcceptedAnalytics();
}

function setAnalyticsConsent(next) {
  if (cachedConsent === next) {
    applyGrantedGa4Loaders(ga4ShouldLoad());
    applyPostHogConsentState();
    return;
  }
  cachedConsent = next;
  publishConsentState();
}

function markUserInteracted() {
  userHasInteracted = true;
}

function markAnalyticsAccepted() {
  markUserInteracted();
  pendingAnalyticsPurposeDecline = false;
  pendingAnalyticsVendorDecline = false;
  pendingPostHogVendorDecline = false;
  userClickedDisagree = false;
  allowPostHogInit();
  setAnalyticsConsent('accepted');
}

function markAnalyticsDeclined() {
  markUserInteracted();
  userClickedDisagree = true;
  setAnalyticsConsent('declined');
  applyPostHogConsentState();
}

/** @returns {'accepted' | 'declined' | null} */
export const getAnalyticsConsent = () => cachedConsent;

export function hasAcceptedAnalytics() {
  if (cachedConsent === 'declined' || userClickedDisagree) {
    return false;
  }
  if (pendingAnalyticsPurposeDecline || pendingAnalyticsVendorDecline) {
    return false;
  }

  const Didomi = getDidomi();
  if (Didomi) {
    try {
      const status = Didomi.getCurrentUserStatus();
      if (isDidomiPurposeConsentDenied(status) || isDidomiAnalyticsVendorDenied(status)) {
        return false;
      }
      if (isAnalyticsEnabledInStatus(status)) {
        return true;
      }
      if (isDidomiOptOutRegime() && !isAnalyticsExplicitlyDeclined(status)) {
        return true;
      }
    } catch (e) {
      /* fall through */
    }
  }
  return cachedConsent === 'accepted';
}

export const hasAcceptedPostHog = () => {
  if (!hasAcceptedAnalytics()) {
    return false;
  }
  if (pendingPostHogVendorDecline || pendingAnalyticsPurposeDecline) {
    return false;
  }
  const Didomi = getDidomi();
  if (!Didomi) {
    return false;
  }
  try {
    const status = Didomi.getCurrentUserStatus();
    if (
      isDidomiPurposeConsentDenied(status)
      || isConfiguredAnalyticsPurposeDisabled(status)
    ) {
      return false;
    }
    if (isPostHogExplicitlyDenied(status)) {
      return false;
    }
    return isPostHogEnabledInStatus(status);
  } catch (e) {
    return false;
  }
};

export {
  setAnalyticsConsent,
  vendorLookupKeys,
  isVendorEnabledInStatus,
  isAnalyticsEnabledInStatus,
  isPostHogEnabledInStatus,
};

export const onConsentChange = (callback) => {
  if (typeof callback !== 'function') return () => {};
  changeListeners.add(callback);
  return () => changeListeners.delete(callback);
};

function registerDidomiListeners() {
  if (typeof window === 'undefined') return;

  window.didomiEventListeners = window.didomiEventListeners || [];
  window.didomiOnReady = window.didomiOnReady || [];

  window.didomiEventListeners.push({
    event: 'notice.clickagree',
    listener: () => {
      markAnalyticsAccepted();
      scheduleConsentResync();
    },
  });

  window.didomiEventListeners.push({
    event: 'notice.clickdisagree',
    listener: markAnalyticsDeclined,
  });

  window.didomiEventListeners.push({
    event: 'preferences.clickagreetoall',
    listener: () => {
      markAnalyticsAccepted();
      scheduleConsentResync();
    },
  });

  window.didomiEventListeners.push({
    event: 'preferences.clickdisagreetoall',
    listener: markAnalyticsDeclined,
  });

  window.didomiEventListeners.push({
    event: 'preferences.shown',
    listener: markUserInteracted,
  });

  window.didomiEventListeners.push({
    event: 'preferences.clicksavechoices',
    listener: () => {
      markUserInteracted();
      syncProfileConsentFromDidomi();
      syncConsentAfterUserAction();
    },
  });

  window.didomiEventListeners.push({
    event: 'preferences.clickpurposedisagree',
    listener: (data) => {
      markUserInteracted();
      if (data && isAnalyticsRelatedPurpose(data.purposeId)) {
        pendingAnalyticsPurposeDecline = true;
        pendingPostHogVendorDecline = true;
        markAnalyticsDeclined();
        return;
      }
      scheduleConsentResync();
    },
  });

  window.didomiEventListeners.push({
    event: 'preferences.clickpurposeagree',
    listener: (data) => {
      markUserInteracted();
      if (data && isAnalyticsRelatedPurpose(data.purposeId)) {
        pendingAnalyticsPurposeDecline = false;
        pendingPostHogVendorDecline = false;
      }
      scheduleConsentResync();
    },
  });

  window.didomiEventListeners.push({
    event: 'preferences.clickvendorsdisagree',
    listener: (data) => {
      markUserInteracted();
      if (data && isAnalyticsRelatedVendor(data.vendorId)) {
        pendingAnalyticsVendorDecline = true;
        markAnalyticsDeclined();
        return;
      }
      if (data && isPostHogRelatedVendor(data.vendorId)) {
        pendingPostHogVendorDecline = true;
        syncProfileConsentFromDidomi();
        scheduleConsentResync();
        return;
      }
      scheduleConsentResync();
    },
  });

  window.didomiEventListeners.push({
    event: 'preferences.clickvendorsagree',
    listener: (data) => {
      markUserInteracted();
      if (data && isAnalyticsRelatedVendor(data.vendorId)) {
        pendingAnalyticsVendorDecline = false;
      }
      if (data && isPostHogRelatedVendor(data.vendorId)) {
        pendingPostHogVendorDecline = false;
      }
      scheduleConsentResync();
    },
  });

  window.didomiEventListeners.push({
    event: 'consent.changed',
    listener: () => {
      if (!getDidomi()) return;
      const didomi = getDidomi();
      if (!userHasInteracted && didomi.isConsentRequired() && cachedConsent === null) {
        return;
      }
      syncProfileConsentFromDidomi();
      scheduleConsentResync();
    },
  });

  function bindDidomiConsentObserver(Didomi) {
    if (!Didomi || Didomi.__oeProfileConsentObserverBound) {
      return;
    }
    Didomi.__oeProfileConsentObserverBound = true;
    if (typeof Didomi.on === 'function') {
      Didomi.on('consent.changed', syncProfileConsentFromDidomi);
    }
  }

  window.didomiOnReady.push((Didomi) => {
    window.Didomi = Didomi;
    bindDidomiConsentObserver(Didomi);

    if (!Didomi.isConsentRequired()) {
      applyDidomiVendorStatusToCache();
      if (cachedConsent !== null) {
        publishConsentState();
      }
      return;
    }

    applyDidomiVendorStatusToCache();
    if (cachedConsent !== null) {
      publishConsentState();
    }
  });
}

registerDidomiListeners();

setPostHogCaptureAllowedChecker(() => hasAcceptedAnalytics() && hasAcceptedPostHog());
